// Shared game-status helpers. The gameStatus || status fallback chain is
// deliberate: different endpoints return different DTO shapes.

// `textColor` is what to print ON `color` when it is used as a badge background.
// The gold of a completed game is far too light to carry white text — it reads as
// an empty pill — so those two get dark ink instead.
export const STATUS_CONFIG = {
  OPEN: { label: 'Setup Phase', color: '#2e7d32', textColor: '#ffffff', description: 'Accepting players' },
  STARTED: { label: 'Started', color: '#1565c0', textColor: '#ffffff', description: 'Picks locked, awaiting results' },
  IN_PROGRESS: { label: 'In Progress', color: '#4f46e5', textColor: '#ffffff', description: 'Game underway' },
  COMPLETED_WINNER: { label: 'Winner', color: '#FFD700', textColor: '#3d2f00', description: 'Winner decided' },
  COMPLETED_TIE: { label: 'Shared Winners', color: '#FFD700', textColor: '#3d2f00', description: 'Split prize' },
  CANCELLED_PLAYER: { label: 'Cancelled', color: '#c62828', textColor: '#ffffff', description: 'Cancelled by player' },
  CANCELLED_ADMIN: { label: 'Cancelled', color: '#c62828', textColor: '#ffffff', description: 'Cancelled by admin' },
  CANCELLED_AUTO: { label: 'Cancelled', color: '#c62828', textColor: '#ffffff', description: 'Auto-cancelled' },
};

export const FINISHED_STATUSES = ['COMPLETED_WINNER', 'COMPLETED_TIE', 'CANCELLED_PLAYER', 'CANCELLED_ADMIN', 'CANCELLED_AUTO'];

export const getGameStatus = (game) => game.gameStatus || game.status || 'OPEN';

export const getStatusConfig = (game) => STATUS_CONFIG[getGameStatus(game)] || STATUS_CONFIG.OPEN;

export const isGameCompleted = (game) => {
  const status = getGameStatus(game);
  return status === 'COMPLETED_WINNER' || status === 'COMPLETED_TIE';
};

// Trophy string for the games list. GameDetailsPage has its own richer
// winner display returning an object — the two shapes differ on purpose.
//
// Handles, because that is what the roster carries: /api/customer/games/my-games used to
// serialise the Game entity and hand every player the real name, email, postal address and
// date of birth of everybody else in the game. It sends GameDetailDto now, the same
// projection the game screen reads, so the list and the screen name people the same way.
export const getWinnerDisplay = (game) => {
  const status = getGameStatus(game);
  if (status === 'COMPLETED_WINNER' && game.remainingPlayers?.length === 1) {
    return `🏆 ${game.remainingPlayers[0].displayName}`;
  }
  if (status === 'COMPLETED_TIE' && game.remainingPlayers?.length >= 2) {
    const names = game.remainingPlayers.map(p => p.displayName).join(' & ');
    return `🏆 ${names}`;
  }
  return null;
};

/**
 * Whether a game has aged out of the active tab.
 *
 * This used to be the rule itself: finished, and completed or cancelled more than seven days ago,
 * computed here over games already downloaded. It is now read from `archived`, which the API
 * derives — see GameArchivePolicy on the backend.
 *
 * The move was forced rather than tidy-minded. The admin games list has to filter on archived
 * inside a SQL query, where a rule living in a browser is no help, so the rule had to exist server
 * side. Once it did, keeping this copy meant two definitions of one fact: they agree right up until
 * somebody changes seven to fourteen in one of them, and nothing fails when they diverge.
 *
 * The local fallback stays for the endpoints that do not carry the flag, and is deliberately the
 * same seven days, so a screen reading a single game does not disagree with the list it came from.
 * Anything that filters lists should be reading the flag.
 *
 * /api/games/{id} used to be the reason this fallback existed — it returned a bare Game. It
 * returns GameDetailDto now, which carries `archived`, so the game screen takes the server's
 * answer like everything else and the arithmetic below is one caller further from being used.
 */
export const isArchived = (game) => {
  if (typeof game.archived === 'boolean') return game.archived;

  const status = getGameStatus(game);
  if (!FINISHED_STATUSES.includes(status)) return false;
  const finishedAt = game.completedAt || game.cancelledAt;
  if (!finishedAt) return false;
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
  return new Date(finishedAt) < sevenDaysAgo;
};

/**
 * "Rolled over in week 3", or "Rollover 2 · week 7" once there has been more than one (backlog
 * item 49). The count and the week are the server's, from the rollovers it recorded; nothing
 * here works out whether a game rolled over.
 */
export const rolloverLabel = (game) =>
  game.rolloverCount > 1
    ? `Rollover ${game.rolloverCount} · week ${game.lastRolloverWeek}`
    : `Rolled over in week ${game.lastRolloverWeek}`;
